import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { InventoryDetailItem, InventoryStatus, InventoryTransaction } from '../types/inventoryDetail.types';

type InventoryRow = Record<string, unknown>;

function textValue(value: unknown, fallback = '') {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function numberValue(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function resolveStatus(row: InventoryRow): InventoryStatus {
  const rawStatus = textValue(row.status_code);
  if (rawStatus === 'inactive' || rawStatus === 'disabled' || row.is_active === false) return 'inactive';

  const currentStock = numberValue(row.current_stock ?? row.stock_quantity ?? row.quantity ?? row.opening_stock);
  const minimumStock = numberValue(row.minimum_stock ?? row.min_stock);
  if (currentStock <= 0) return 'out_of_stock';
  if (minimumStock > 0 && currentStock <= minimumStock) return 'low_stock';
  return 'in_stock';
}

function mapInventoryDetailItem(row: InventoryRow): InventoryDetailItem {
  // Try to parse specifications if it's a JSON string or object
  let specs: Record<string, any> = {};
  try {
    if (typeof row.specifications === 'string') {
      specs = JSON.parse(row.specifications);
    } else if (typeof row.specifications === 'object' && row.specifications !== null) {
      specs = row.specifications as Record<string, any>;
    }
  } catch (e) {
    console.warn('[mapInventoryDetailItem] Failed to parse specifications:', e);
  }

  return {
    id: textValue(row.id),
    company_id: textValue(row.company_id),
    code: textValue(row.code, textValue(row.item_code, 'VT-000')),
    name: textValue(row.name, textValue(row.item_name, 'Vt t cha t tn')),
    category_name: textValue(row.category_name, textValue(row.group_name)) || null,
    unit: textValue(row.unit, 'Ci'),
    current_stock: numberValue(row.current_stock ?? row.stock_quantity ?? row.quantity ?? row.opening_stock),
    minimum_stock: numberValue(row.minimum_stock ?? row.min_stock),
    unit_price: numberValue(row.unit_price ?? row.purchase_price ?? row.last_unit_cost),
    status_code: resolveStatus(row),
    thumbnail_url: textValue(row.thumbnail_url, textValue(row.image_url)) || null,
    supplier_name: textValue(row.supplier_name, textValue(row.vendor_name)) || null,
    specification: textValue(row.specification, textValue(row.sub_name, textValue(row.description))) || null,
    warehouse_name: textValue(row.warehouse_name, textValue(row.location_name)) || null,
    storage_location: textValue(row.storage_location, textValue(row.location)) || null,
    origin_name: textValue(row.origin_name, specs.origin) || null,
    manufacturer_code: textValue(row.manufacturer_code, specs.manufacturer_code) || null,
    note: textValue(row.note, textValue(row.notes)) || null,
    // Extended Specs from JSONB
    height: textValue(specs.height, '20 - 40 cm'),
    internode_height: textValue(specs.internode_height, '10 - 15 cm'),
    canopy_diameter: textValue(specs.canopy_diameter, '15 - 25 cm'),
    trunk_diameter: textValue(specs.trunk_diameter, '5 - 10 cm'),
    base_diameter: textValue(specs.base_diameter, '10 - 15 cm'),
    pot_spec: textValue(specs.pot_spec, 'Chu nha treo'),
    substrate: textValue(specs.substrate, 't hu c trn x da, perlite'),
    water_requirement: textValue(specs.water_requirement, 'Trung bnh'),
    light_requirement: textValue(specs.light_requirement, 'Nng nh - Bóng râm'),
    seasonality: textValue(specs.seasonality, 'Quanh năm'),
    expiry_date: textValue(specs.expiry_date, '-'),
    updated_at: textValue(row.updated_at) || null,
    created_at: textValue(row.created_at) || null,
  };
}

export const inventoryDetailService = {
  async getInventoryDetail(id: string, userId: string, companyId: string): Promise<InventoryDetailItem | null> {
    await permissionEngine.assert(userId, 'inventory.items.view', { companyId, inventoryItemId: id });

    const { data, error } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('id', id)
      .eq('company_id', companyId)
      .maybeSingle();

    if (error) throw error;
    return data ? mapInventoryDetailItem(data as InventoryRow) : null;
  },

  async getTransactionHistory(itemId: string, userId: string, companyId: string): Promise<InventoryTransaction[]> {
    await permissionEngine.assert(userId, 'inventory.stock.view', { companyId, inventoryItemId: itemId });

    const { data, error } = await supabase
      .from('inventory_transactions')
      .select('*')
      .eq('item_id', itemId)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) return [];

    return (data ?? []).map((row) => ({
      id: String(row.id),
      item_id: String(row.item_id),
      created_at: String(row.created_at),
      transaction_type: row.transaction_type ?? row.type ?? null,
      quantity: Number(row.quantity ?? 0),
      unit_cost: row.unit_cost === null || row.unit_cost === undefined ? null : Number(row.unit_cost),
      user_name: row.user_name ?? row.created_by_name ?? null,
      note: row.note ?? row.notes ?? null,
    }));
  },

  async getWarehouseStock(itemId: string, companyId: string): Promise<{ warehouse_name: string; stock: number }[]> {
    const { data, error } = await supabase
      .from('inventory_transactions')
      .select(`
        quantity,
        transaction_type,
        warehouse:warehouses(name)
      `)
      .eq('item_id', itemId)
      .eq('company_id', companyId);

    if (error) return [];

    const map = new Map<string, number>();
    (data as any[]).forEach((t) => {
      const whName = t.warehouse?.name || 'Kho mc nh';
      const qty = Number(t.quantity || 0);
      const current = map.get(whName) || 0;

      if (t.transaction_type === 'stock_in' || t.transaction_type === 'import') {
        map.set(whName, current + qty);
      } else if (t.transaction_type === 'stock_out' || t.transaction_type === 'export') {
        map.set(whName, current - qty);
      } else if (t.transaction_type === 'adjustment') {
        map.set(whName, current + qty);
      }
    });

    return Array.from(map.entries()).map(([name, stock]) => ({
      warehouse_name: name,
      stock,
    }));
  },
};
